import React, { useState, useEffect } from "react";
import {
  Box, Typography, Grid, Button, Chip,
  Stepper, Step, StepLabel, Paper, TextField, InputAdornment,
  Avatar, Divider, Alert, CircularProgress, Checkbox,
} from "@mui/material";
import {
  People, CalendarMonth, Business, CheckCircle, Search, ErrorOutline,
} from "@mui/icons-material";
import { BASE_URL } from "../../services/configUrls";

const FREQUENCY_OPTIONS = [
  { label: "Weekly",      value: "weekly",      desc: "Every week"     },
  { label: "Fortnightly", value: "fortnightly", desc: "Every 2 weeks"  },
  { label: "Monthly",     value: "monthly",     desc: "Once a month"   },
  { label: "Quarterly",   value: "quarterly",   desc: "Every 3 months" },
  { label: "Bi-Annually", value: "half_yearly", desc: "Every 6 months" },
  { label: "Annual",      value: "annual",      desc: "Once a year"    },
];

const STEPS = ["HR Count", "Frequency", "Select Corporates", "Confirm"];

const COMPANY_TYPES = [
  { label: "All",           value: "all",           color: "#374151", bg: "#f3f4f6" },
  { label: "IT & Tech",     value: "it",            color: "#1d4ed8", bg: "#eff6ff" },
  { label: "Finance",       value: "finance",       color: "#15803d", bg: "#f0fdf4" },
  { label: "Manufacturing", value: "manufacturing", color: "#b45309", bg: "#fef3c7" },
  { label: "Healthcare",    value: "healthcare",    color: "#be185d", bg: "#fdf2f8" },
  { label: "Consulting",    value: "consulting",    color: "#6d28d9", bg: "#f5f3ff" },
  { label: "Others",        value: "others",        color: "#4b5563", bg: "#f9fafb" },
];

const IT_KEYWORDS            = ["tech", "software", "systems", "digital", "infosys", "wipro", "tcs", "hcl", "cognizant", "accenture", "ibm", "oracle", "microsoft", "google", "amazon", "data", "cyber", "cloud", "infotech", "solutions", "computer", "network", "analytics"];
const FINANCE_KEYWORDS       = ["bank", "finance", "financial", "capital", "invest", "insurance", "hdfc", "icici", "axis", "kotak", "sbi", "bajaj", "credit", "fund", "asset", "wealth", "payment", "securities", "fincorp"];
const MANUFACTURING_KEYWORDS = ["industries", "industry", "manufacturing", "motors", "auto", "steel", "cement", "chemical", "textile", "energy", "power", "oil", "gas", "mining", "construction", "infra", "tata", "mahindra", "l&t", "bharat", "national"];
const HEALTHCARE_KEYWORDS    = ["health", "hospital", "medical", "pharma", "care", "clinic", "bio", "life", "apollo", "fortis", "medic", "wellness", "diagnostic", "labs"];
const CONSULTING_KEYWORDS    = ["consult", "advisory", "management", "strategy", "deloitte", "kpmg", "pwc", "ernst", "bain", "bcg", "associates"];

const detectCompanyType = (name = "") => {
  const n = name.toLowerCase();
  if (CONSULTING_KEYWORDS.some((k)    => n.includes(k))) return "consulting";
  if (FINANCE_KEYWORDS.some((k)       => n.includes(k))) return "finance";
  if (HEALTHCARE_KEYWORDS.some((k)    => n.includes(k))) return "healthcare";
  if (MANUFACTURING_KEYWORDS.some((k) => n.includes(k))) return "manufacturing";
  if (IT_KEYWORDS.some((k)            => n.includes(k))) return "it";
  return "others";
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(amount);

const getPricingInfo = (hrCount, isBulk) => {
  if (!hrCount) return null;
  if (isBulk)       return { pricePerHr: null,  totalPrice: null,            tierLabel: "Bulk Discount", discountNote: "Best discount available" };
  if (hrCount <= 4) return { pricePerHr: 20000, totalPrice: 20000 * hrCount, tierLabel: "Standard",     discountNote: null };
  return                   { pricePerHr: 15000, totalPrice: 15000 * hrCount, tierLabel: "Value Pack",   discountNote: "25% cheaper vs Standard" };
};

const getTierStyle = (hrCount, isBulk) => {
  if (!hrCount) return null;
  if (isBulk)       return { label: "🎉 Bulk Discount",        bgcolor: "#faf5ff", color: "#7c3aed" };
  if (hrCount <= 4) return { label: "Standard",                bgcolor: "#eff6ff", color: "#1d4ed8" };
  return                   { label: "✅ Value Pack (25% off)", bgcolor: "#f0fdf4", color: "#15803d" };
};

// ── max = hrCount * 3, works for every number ──
const getCorporateLimits = (hrCount, isBulk) => {
  if (!hrCount) return null;
  if (isBulk) return { max: 20 * 3 };         // 20+ → 60
  if (hrCount === 1) return { max: 5 };        // 1 HR → 5 companies
  return { max: hrCount * 3 };                 // 2+ HRs → hrCount × 3
};
const getLogoLetters = (name = "") =>
  name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("") ||
  name.slice(0, 2).toUpperCase();

async function safeJson(res) {
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json"))
    throw new Error(`Server returned non-JSON response (HTTP ${res.status}).`);
  return res.json();
}

async function fetchUserId() {
  const token = localStorage.getItem("accessToken");
  if (!token) throw new Error("No access token found. Please log in again.");
  const res = await fetch(`${BASE_URL}/api/hr-campus/fetch_token`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await safeJson(res).catch(() => ({}));
    throw new Error(err.detail ?? "Failed to fetch user info");
  }
  return (await safeJson(res)).user_id;
}

async function fetchExistingRequest(userId) {
  const token = localStorage.getItem("accessToken");
  const res   = await fetch(`${BASE_URL}/api/hr-campus/requests/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const data = await safeJson(res).catch(() => []);
  return Array.isArray(data) && data.length > 0 ? data[0] : null;
}

async function fetchApprovedCompanies() {
  const token = localStorage.getItem("accessToken");
  const res   = await fetch(`${BASE_URL}/api/hr-campus/companies`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await safeJson(res).catch(() => ({}));
    throw new Error(err.detail ?? `Failed to load companies (${res.status})`);
  }
  const data = await safeJson(res);
  return data.map((d) => d.company_name);
}

async function submitHRCampusRequest(payload) {
  const token = localStorage.getItem("accessToken");
  const res   = await fetch(`${BASE_URL}/api/hr-campus/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await safeJson(res).catch(() => ({}));
    throw new Error(err.detail ?? "Submission failed");
  }
  return safeJson(res);
}

export default function GetHRIntoCampus() {
  const [activeStep, setActiveStep]                 = useState(0);
  const [hrCount, setHrCount]                       = useState(1);
  const [hrDisplay, setHrDisplay]                   = useState("1");
  const [frequency, setFrequency]                   = useState(null);
  const [selectedCorporates, setSelectedCorporates] = useState([]);
  const [searchQuery, setSearchQuery]               = useState("");
  const [activeTypeFilter, setActiveTypeFilter]     = useState("all");
  const [submitting, setSubmitting]                 = useState(false);
  const [submitError, setSubmitError]               = useState("");
  const [companies, setCompanies]                   = useState([]);
  const [loadingCompanies, setLoadingCompanies]     = useState(false);
  const [companiesError, setCompaniesError]         = useState("");
  const [userId, setUserId]                         = useState(null);
  const [userIdError, setUserIdError]               = useState("");
  const [submittedRequest, setSubmittedRequest]     = useState(null);
  const [initialLoading, setInitialLoading]         = useState(true);

  const isBulk = hrDisplay === "20+";
  const limits  = getCorporateLimits(hrCount, isBulk);
  const pricing = getPricingInfo(hrCount, isBulk);
  const tier    = getTierStyle(hrCount, isBulk);

  const handleDropdownChange = (e) => {
    const val = e.target.value;
    setHrDisplay(val);
    setHrCount(val === "20+" ? 20 : parseInt(val, 10));
    setSelectedCorporates([]);
  };

  const enrichedCompanies = companies.map((name, index) => ({
    name,
    type:   detectCompanyType(name),
    number: index + 1,
  }));

  const typeCounts = COMPANY_TYPES.reduce((acc, t) => {
    acc[t.value] = t.value === "all"
      ? enrichedCompanies.length
      : enrichedCompanies.filter((c) => c.type === t.value).length;
    return acc;
  }, {});

  const filteredCorporates = enrichedCompanies.filter((c) => {
    const matchSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchType   = activeTypeFilter === "all" || c.type === activeTypeFilter;
    return matchSearch && matchType;
  });

  useEffect(() => {
    async function init() {
      try {
        const uid      = await fetchUserId();
        setUserId(uid);
        const existing = await fetchExistingRequest(uid);
        if (existing) {
          setSubmittedRequest(existing);
          localStorage.setItem("hr_campus_submitted_request", JSON.stringify(existing));
        }
      } catch (e) {
        setUserIdError(e.message);
      } finally {
        setInitialLoading(false);
      }
    }
    init();
  }, []);

  useEffect(() => {
    if (activeStep === 2 && companies.length === 0 && !loadingCompanies) {
      setLoadingCompanies(true);
      setCompaniesError("");
      fetchApprovedCompanies()
        .then(setCompanies)
        .catch((e) => setCompaniesError(e.message))
        .finally(() => setLoadingCompanies(false));
    }
  }, [activeStep]);

  const handleCorporateToggle = (name) => {
    setSelectedCorporates((prev) => {
      if (prev.includes(name)) return prev.filter((c) => c !== name);
      if (limits && prev.length >= limits.max) return prev;
      return [...prev, name];
    });
  };

  const canProceedStep = () => {
    if (activeStep === 0) return hrCount >= 1;
    if (activeStep === 1) return !!frequency;
    if (activeStep === 2)
      return limits &&
        selectedCorporates.length >= 1 &&
        selectedCorporates.length <= limits.max;
    return true;
  };

  const handleNext = async () => {
    if (activeStep === STEPS.length - 1) {
      setSubmitting(true);
      setSubmitError("");
      try {
        const result = await submitHRCampusRequest({
          user_id:            userId,
          hr_count:           isBulk ? "20+" : hrCount,
          frequency,
          selected_companies: selectedCorporates,
          price_per_hr:       pricing?.pricePerHr ?? "custom",
          total_price:        pricing?.totalPrice  ?? "custom",
        });
        localStorage.setItem("hr_campus_submitted_request", JSON.stringify(result));
        setSubmittedRequest(result);
      } catch (e) {
        setSubmitError(e.message);
      } finally {
        setSubmitting(false);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setSubmitError("");
    setActiveStep((prev) => prev - 1);
  };

  if (initialLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ color: "#4a90e2" }} />
        <Typography variant="caption" color="#6b7280" display="block" mt={1}>Loading…</Typography>
      </Box>
    );
  }

  if (userIdError) {
    return (
      <Box sx={{ maxWidth: 480, mx: "auto", py: 5, textAlign: "center" }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography fontSize={13}>{userIdError}</Typography>
        </Alert>
      </Box>
    );
  }

  if (submittedRequest) {
    return (
      <Box sx={{ maxWidth: 500, mx: "auto", textAlign: "center", py: 5 }}>
        <CheckCircle sx={{ fontSize: 60, color: "#22c55e", mb: 1.5 }} />
        <Typography variant="h6" fontWeight={700} color="#111827" mb={0.5}>
          Request Submitted!
        </Typography>
        <Typography variant="body2" color="#6b7280" mb={2.5}>
          Your HR campus invite has been scheduled. Our team will review and get back to you.
        </Typography>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, textAlign: "left", borderRadius: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Typography variant="caption" color="#9ca3af">HR Invited</Typography>
              <Typography fontWeight={600} fontSize={14}>{submittedRequest.hr_count} HRs</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="#9ca3af">Frequency</Typography>
              <Typography fontWeight={600} fontSize={14} sx={{ textTransform: "capitalize" }}>
                {submittedRequest.frequency?.replace("_", " ")}
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="caption" color="#9ca3af">Status</Typography>
              <Box mt={0.5}>
                <Chip
                  label={
                    submittedRequest.status === "approved" ? "Approved"
                    : submittedRequest.status === "rejected" ? "Rejected"
                    : "Pending Review"
                  }
                  size="small"
                  sx={{
                    bgcolor:
                      submittedRequest.status === "approved" ? "#dcfce7"
                      : submittedRequest.status === "rejected" ? "#fee2e2"
                      : "#fef9c3",
                    color:
                      submittedRequest.status === "approved" ? "#166534"
                      : submittedRequest.status === "rejected" ? "#991b1b"
                      : "#854d0e",
                    fontWeight: 600, fontSize: 11,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="#9ca3af">Submitted On</Typography>
              <Typography fontWeight={600} fontSize={13}>
                {new Date(submittedRequest.created_at).toLocaleDateString("en-IN", {
                  day: "2-digit", month: "short", year: "numeric",
                })}
              </Typography>
            </Grid>
            {submittedRequest.selected_companies?.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" color="#9ca3af">
                  Corporates ({submittedRequest.selected_companies.length})
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                  {submittedRequest.selected_companies.map((name) => (
                    <Chip key={name} label={name} size="small"
                      sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontSize: 11 }} />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700} color="#111827">
          Get HR Into Your Campus
        </Typography>
        <Typography variant="caption" color="#6b7280">
          Plan and schedule HR visits to your campus in a few easy steps.
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel sx={{
              "& .MuiStepLabel-label":             { fontSize: "12px", fontWeight: 500 },
              "& .MuiStepIcon-root.Mui-active":    { color: "#4a90e2" },
              "& .MuiStepIcon-root.Mui-completed": { color: "#22c55e" },
            }}>
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>


      {/* ══ Step 0 — HR Count ══ */}
      {activeStep === 0 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <People sx={{ color: "#4a90e2", fontSize: 20 }} />
            <Typography fontWeight={600} fontSize={14}>
              How many HRs do you want to invite?
            </Typography>
          </Box>

          {/* Dropdown */}
          <Box sx={{ display: "flex", flexDirection: "column", mb: 2.5 }}>
            <Box sx={{ position: "relative", width: 220 }}>
              <select
                value={hrDisplay}
                onChange={handleDropdownChange}
                style={{
                  width: "100%", height: 42,
                  padding: "0 36px 0 14px",
                  fontSize: 14, fontWeight: 500,
                  color: "#111827", background: "white",
                  border: "1.5px solid #d1d5db", borderRadius: 8,
                  appearance: "none", WebkitAppearance: "none",
                  cursor: "pointer", outline: "none",
                }}
              >
                {Array.from({ length: 19 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={String(n)}> {n}</option>
                ))}
                <option value="20+"> 20+</option>
              </select>
              <Box sx={{
                position: "absolute", right: 10, top: "50%",
                transform: "translateY(-50%)", pointerEvents: "none",
                color: "#6b7280", display: "flex", alignItems: "center",
              }}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </Box>
            </Box>
            <Typography fontSize={11} color="#9ca3af" mt={0.75}>
              HR{hrCount > 1 ? "s" : ""}
            </Typography>
          </Box>

          {/* Tier badge */}
          

          {/* Pricing Note */}
          <Paper variant="outlined" sx={{
            p: 2, borderRadius: 2, bgcolor: "#f9fafb", border: "1px solid #e5e7eb",
          }}>
            <Typography fontSize={11} fontWeight={700}
              sx={{ letterSpacing: 0.4, mb: 1, textTransform: "uppercase", color: "#6b7280" }}>
              Pricing{" "}
              <Box component="span" sx={{ color: "#ef4444" }}>Note</Box>
            </Typography>
            {[
              { range: "1 – 4 HRs",  price: "₹20,000 per HR",        color: "#1d4ed8", bg: "#eff6ff", active: !isBulk && hrCount >= 1  && hrCount <= 4  },
              { range: "5 – 19 HRs", price: "₹15,000 per HR",        color: "#15803d", bg: "#f0fdf4", active: !isBulk && hrCount >= 5  && hrCount <= 19 },
              { range: "20+ HRs",    price: "Best discount available", color: "#7c3aed", bg: "#f5f3ff", active: isBulk },
            ].map((item) => (
              <Box key={item.range} sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: 1.25, py: 0.75, mb: 0.5, borderRadius: 1.5,
                bgcolor: item.active ? item.bg : "transparent",
                border:  item.active ? `1.5px solid ${item.color}30` : "1.5px solid transparent",
                transition: "all 0.2s ease",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{
                    width: 6, height: 6, borderRadius: "50%",
                    bgcolor: item.active ? item.color : "#d1d5db", flexShrink: 0,
                  }} />
                  <Typography fontSize={12} fontWeight={item.active ? 700 : 400}
                    color={item.active ? item.color : "#6b7280"}>
                    {item.range}
                  </Typography>
                </Box>
                <Typography fontSize={12} fontWeight={item.active ? 700 : 400}
                  color={item.active ? item.color : "#9ca3af"}>
                  {item.price}
                </Typography>
              </Box>
            ))}
          </Paper>

          {/* Corporate limit hint — shows max = hrCount × 3 */}
          {limits && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2, py: 0.5 }}
              icon={<Business sx={{ fontSize: 16 }} />}>
              <Typography fontSize={12}>
                For <strong>{isBulk ? "20+" : hrCount} HR{hrCount > 1 ? "s" : ""}</strong> → you can select up to{" "}
                <strong>{limits.max} corporates</strong> in Step 3.
              </Typography>
            </Alert>
          )}
        </Paper>
      )}


      {/* ══ Step 1 — Frequency ══ */}
      {activeStep === 1 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CalendarMonth sx={{ color: "#4a90e2", fontSize: 20 }} />
            <Typography fontWeight={600} fontSize={14}>
              How frequently do you want to conduct this event?
            </Typography>
          </Box>
          <Grid container spacing={1}>
            {FREQUENCY_OPTIONS.map((opt) => (
              <Grid item xs={12} sm={6} md={4} key={opt.value}>
                <Paper variant="outlined" onClick={() => setFrequency(opt.value)}
                  sx={{
                    px: 1.5, py: 1, borderRadius: 2, cursor: "pointer",
                    border:  frequency === opt.value ? "2px solid #4a90e2" : "1px solid #e5e7eb",
                    bgcolor: frequency === opt.value ? "#eff6ff" : "white",
                    transition: "all 0.15s ease",
                    "&:hover": { borderColor: "#4a90e2", bgcolor: "#f0f9ff" },
                  }}>
                  <Typography fontWeight={600} fontSize={13}
                    color={frequency === opt.value ? "#1d4ed8" : "#111827"}>
                    {opt.label}
                  </Typography>
                  <Typography variant="caption" color="#9ca3af" fontSize={11}>{opt.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}


      {/* ══ Step 2 — Select Corporates ══ */}
      {activeStep === 2 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Business sx={{ color: "#4a90e2", fontSize: 20 }} />
              <Typography fontWeight={600} fontSize={14}>Select Corporates</Typography>
            </Box>
            <Chip
              label={`${selectedCorporates.length} / ${limits?.max} selected`}
              color={selectedCorporates.length >= 1 ? "success" : "default"}
              size="small" sx={{ fontSize: 11 }}
            />
          </Box>

          <Alert
            severity={selectedCorporates.length >= 1 ? "success" : "warning"}
            sx={{ mb: 1.5, borderRadius: 2, py: 0.5 }}
          >
            <Typography fontSize={12}>
              Select up to <strong>{limits?.max} corporates</strong> for{" "}
              <strong>{isBulk ? "20+" : hrCount} HR{hrCount > 1 ? "s" : ""}</strong>.{" "}
              <strong>{selectedCorporates.length}</strong> selected so far.
            </Typography>
          </Alert>

          <TextField
            fullWidth size="small" placeholder="Search company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 16, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
          />

          {loadingCompanies && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: "#4a90e2" }} />
              <Typography variant="caption" color="#6b7280" display="block" mt={1}>
                Loading companies…
              </Typography>
            </Box>
          )}

          {companiesError && !loadingCompanies && (
            <Alert severity="error" icon={<ErrorOutline />} sx={{ borderRadius: 2 }}>
              <Typography fontSize={12}>{companiesError}</Typography>
            </Alert>
          )}

          {!loadingCompanies && !companiesError && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>

              <Box sx={{
                width: 145, flexShrink: 0,
                position: "sticky", top: 16, alignSelf: "flex-start",
                display: "flex", flexDirection: "column", gap: 0.5,
              }}>
                <Typography fontSize={10} fontWeight={700} color="#9ca3af"
                  sx={{ letterSpacing: 0.6, mb: 0.5, textTransform: "uppercase" }}>
                  Filter by Type
                </Typography>
                {COMPANY_TYPES.filter((t) =>
                  t.value === "all" || typeCounts[t.value] > 0
                ).map((type) => {
                  const isActive = activeTypeFilter === type.value;
                  return (
                    <Box key={type.value} onClick={() => setActiveTypeFilter(type.value)}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        px: 1.25, py: 0.85, borderRadius: 1.5, cursor: "pointer",
                        bgcolor: isActive ? type.color : "transparent",
                        border:  isActive ? `1.5px solid ${type.color}` : "1.5px solid transparent",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          bgcolor: isActive ? type.color : type.bg,
                          border:  `1.5px solid ${type.color}60`,
                        },
                      }}>
                      <Typography fontSize={12} fontWeight={isActive ? 700 : 500}
                        color={isActive ? "white" : type.color} noWrap>
                        {type.label}
                      </Typography>
                      <Box sx={{
                        bgcolor:  isActive ? "rgba(255,255,255,0.22)" : type.bg,
                        color:    isActive ? "white" : type.color,
                        border:   isActive ? "1px solid rgba(255,255,255,0.35)" : `1px solid ${type.color}35`,
                        borderRadius: "20px", px: 0.75, py: 0.1,
                        fontSize: 10, fontWeight: 700,
                        minWidth: 22, textAlign: "center", ml: 0.75, flexShrink: 0,
                      }}>
                        {typeCounts[type.value]}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ width: "1px", bgcolor: "#e5e7eb", alignSelf: "stretch", flexShrink: 0 }} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {filteredCorporates.length === 0 ? (
                  <Typography variant="caption" color="#9ca3af"
                    display="block" textAlign="center" py={4}>
                    No companies found{searchQuery ? ` matching "${searchQuery}"` : ""}.
                  </Typography>
                ) : (
                  <Grid container spacing={1}>
                    {filteredCorporates.map(({ name, type, number }) => {
                      const isSelected = selectedCorporates.includes(name);
                      const isDisabled = !isSelected && selectedCorporates.length >= (limits?.max ?? 0);
                      const typeInfo   = COMPANY_TYPES.find((t) => t.value === type);
                      return (
                        <Grid item xs={12} sm={6} key={name}>
                          <Paper variant="outlined"
                            onClick={() => !isDisabled && handleCorporateToggle(name)}
                            sx={{
                              px: 1, py: 0.75, borderRadius: 2,
                              cursor:  isDisabled ? "not-allowed" : "pointer",
                              border:  isSelected ? "2px solid #4a90e2" : "1px solid #e5e7eb",
                              bgcolor: isSelected ? "#eff6ff" : isDisabled ? "#f9fafb" : "white",
                              opacity: isDisabled ? 0.5 : 1,
                              display: "flex", alignItems: "center", gap: 0.75,
                              position: "relative", transition: "all 0.15s ease",
                              "&:hover": !isDisabled ? { borderColor: "#4a90e2" } : {},
                            }}>
                            <Checkbox checked={isSelected} disabled={isDisabled} size="small"
                              sx={{ p: 0.5, color: "#9ca3af", "&.Mui-checked": { color: "#4a90e2" } }} />
                            <Avatar sx={{
                              bgcolor: isSelected ? "#4a90e2" : "#e5e7eb",
                              color:   isSelected ? "white"   : "#6b7280",
                              width: 28, height: 28, fontSize: 10, fontWeight: 700, flexShrink: 0,
                            }}>
                              {getLogoLetters(name)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography fontWeight={500} fontSize={12} noWrap
                                sx={{ color: isSelected ? "#1d4ed8" : "#111827" }}>
                                {name}
                              </Typography>
                              <Typography fontSize={10} fontWeight={600}
                                sx={{ color: typeInfo?.color ?? "#9ca3af" }}>
                                {typeInfo?.label ?? "Others"}
                              </Typography>
                            </Box>
                            <Box sx={{
                              position: "absolute", top: 4, right: 6,
                              bgcolor: isSelected ? "#4a90e2" : "#e5e7eb",
                              color:   isSelected ? "white"   : "#9ca3af",
                              borderRadius: "50%", width: 16, height: 16,
                              fontSize: 9, fontWeight: 700,
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              {number}
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      )}


      {/* ══ Step 3 — Confirm ══ */}
      {activeStep === 3 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CheckCircle sx={{ color: "#22c55e", fontSize: 20 }} />
            <Typography fontWeight={600} fontSize={14}>Confirm Your Request</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 2, bgcolor: "#f9fafb", borderRadius: 2 }}>
                <Typography variant="caption" color="#9ca3af" display="block" mb={0.5}>HRs to Invite</Typography>
                <Typography variant="h5" fontWeight={700} color="#1d4ed8">
                  {isBulk ? "20+" : hrCount}
                </Typography>
                <Typography variant="caption" color="#6b7280">HR professionals</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 2, bgcolor: "#f9fafb", borderRadius: 2 }}>
                <Typography variant="caption" color="#9ca3af" display="block" mb={0.5}>Frequency</Typography>
                <Typography fontWeight={700} fontSize={15} color="#111827"
                  sx={{ textTransform: "capitalize" }}>
                  {frequency?.replace("_", " ")}
                </Typography>
                <Typography variant="caption" color="#6b7280">
                  {FREQUENCY_OPTIONS.find((f) => f.value === frequency)?.desc}
                </Typography>
              </Box>
            </Grid>

            
            <Grid item xs={12}>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography fontWeight={600} fontSize={13}>
                  Selected Corporates ({selectedCorporates.length} / {limits?.max})
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {selectedCorporates.map((name) => (
                  <Chip
                    key={name}
                    avatar={
                      <Avatar sx={{ bgcolor: "#4a90e2", fontSize: 9, fontWeight: 700 }}>
                        {getLogoLetters(name)}
                      </Avatar>
                    }
                    label={name} size="small"
                    sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 500, fontSize: 11 }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>

          {submitError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography fontSize={12}>{submitError}</Typography>
            </Alert>
          )}
        </Paper>
      )}


      {/* Navigation */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2.5 }}>
        <Button
          onClick={handleBack} disabled={activeStep === 0}
          variant="outlined" size="small"
          sx={{
            textTransform: "none", borderRadius: 2,
            borderColor: "#e5e7eb", color: "#374151", fontSize: 13,
          }}
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={!canProceedStep() || submitting}
          variant="contained" size="small"
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            textTransform: "none", borderRadius: 2,
            bgcolor: "#4a90e2", "&:hover": { bgcolor: "#357abd" },
            px: 3, fontSize: 13,
          }}
        >
          {activeStep === STEPS.length - 1
            ? submitting ? "Submitting…" : "Submit Request"
            : "Continue"}
        </Button>
      </Box>

    </Box>
  );
}